import { useDeepCompareMemo } from 'use-deep-compare';
import { useAppSelector } from '../../../app/hooks';
import {
  CubeDimensionEnum,
  FeatureEnum,
} from '../../../services/data/impl/cube/cubeSlice';
import {
  FeatureAliases,
  mapToAlias,
} from '../../../services/data/impl/cube/cubeUtils';
import { useVarietiesByCustomer } from '../../../services/entities/hooks/useVarietiesByCustomer';
import { useGetBlocksQuery } from '../../../services/entities/impl/blfApi/endpoints/blocks';
import { CropType } from '../../../services/entities/impl/blfApi/schemas/variety';
import { useFeatureFlags } from '../../../services/featureFlags/hooks/useFeatureFlags';
import { StrictDateRange } from '../../commonUI/DateRangeFilterSelect/types';
import { isFeatureEnum } from '../../commonUtils';
import { filterScansByModelVersion } from '../../ranches/ranchUtils';
import {
  selectCurrAIServiceVersionOverride,
  selectCurrBlockID,
  selectCurrCustomerID,
  selectCurrFeature,
  selectCurrRanchID,
  selectCurrScanID,
} from '../../select/selectSlice';
import useCustomerSummary from './useCustomerSummary';
import { CustomerSummary } from './useCustomerSummaryQuery';

export enum CustomerSummaryFilterEnum {
  ranch = 'ranch',
  block = 'block',
  scan = 'scan',
  feature = 'feature',
  highestAIServiceVersion = 'highestAIServiceVersion',
  disableAIServiceVersionUserOverride = 'disableAIServiceVersionUserOverride',
  showLatestOnly = 'showLatestOnly',
  skip = 'skip',
}

interface FilterParams {
  activeFilters: CustomerSummaryFilterEnum[];
  cropTypes?: CropType[];
  dateRange?: StrictDateRange | null;
}

/**
 * Filters a CustomerSummary array based on the provided filter parameters:
 * - activeFilters: An array of active filter enums to apply.
 * - cropTypes: An optional array of crop types to filter by.
 * - dateRange: An optional date range to filter by.
 *
 * The function applies the following filters based on the active filters:
 * - ranch: Filters by ranchId if provided.
 * - block: Filters by blockId if provided.
 * - scan: Filters by scanId if provided.
 * - feature: Filters by a feature matching the provided alias or original feature name.
 * - showLatestOnly: Keeps only the latest customer summaries for each blockId and className combination.
 * - highestAIServiceVersion: Filters by the highest model version for each AI model.
 * - disableAIServiceVersionUserOverride: Disables the user's AI service version override. If this is not included in the activeFilters, the QA user's AI service version selection in the version dropdown will keep only the selected service version.
 * - skip: Skips filtering entirely and returns null.
 *
 * If cropTypes are provided, the function filters by the crop type of the block associated with the customer summary.
 * If dateRange is provided, the function filters by the date range of the customer summary.
 *
 * Additionally, the function filters out unknown AI models always.
 *
 * @param filterParams - An object containing filter parameters.
 * @returns A new CustomerSummary array filtered based on the criteria, or null if input was null.
 */
export const useFilterCustomerSummary = (
  filterParams: FilterParams
): CustomerSummary[] | null => {
  const { activeFilters, cropTypes, dateRange } = filterParams;
  const currRanchId = useAppSelector(selectCurrRanchID);
  const currBlockId = useAppSelector(selectCurrBlockID);
  const currScanId = useAppSelector(selectCurrScanID);
  const currFeature = useAppSelector(selectCurrFeature);
  const currAIServiceVersionOverride = useAppSelector(
    selectCurrAIServiceVersionOverride
  );
  const currCustomerID = useAppSelector(selectCurrCustomerID);
  const { Developer } = useFeatureFlags();

  const { customerSummary } = useCustomerSummary();

  const filterByCropTypes = cropTypes && cropTypes?.length > 0;
  const { currentData: blocks } = useGetBlocksQuery(undefined, {
    skip: !filterByCropTypes,
  });

  // Get varieties for crop type filtering
  const { getVariety } = useVarietiesByCustomer(currCustomerID);

  return useDeepCompareMemo(() => {
    if (
      !customerSummary.data ||
      activeFilters.includes(CustomerSummaryFilterEnum.skip)
    ) {
      return null;
    }

    // Filter out unknown features (this happens when new AI models are added before the frontend is updated)
    const customerSummaryFiltered = customerSummary.data.filter((summaryItem) =>
      isFeatureEnum(summaryItem[CubeDimensionEnum.className])
    );

    let filteredCustomerSummary = [...customerSummaryFiltered];

    // Filter by crop types if provided
    if (filterByCropTypes) {
      filteredCustomerSummary = filteredCustomerSummary.filter(
        (customerSummary) => {
          const block = blocks?.find(
            (block) =>
              block.id ===
              customerSummary[CubeDimensionEnum.detectionFactBlockId]
          );
          if (!block) return false;

          const variety = getVariety(block.varietyID);
          return variety && cropTypes?.includes(variety.cropType);
        }
      );
    }

    // Filter by date range if provided
    if (dateRange) {
      filteredCustomerSummary = filteredCustomerSummary.filter(
        (customerSummary) => {
          const scanDate = new Date(
            customerSummary[CubeDimensionEnum.scanDate]
          );
          return scanDate >= dateRange.start && scanDate <= dateRange.end;
        }
      );
    }

    // If the user has selected a specific AI service version, filter and show only that version.  Allow this to be disabled so the customerSummaryContext can still get all versions of the services.
    if (
      currAIServiceVersionOverride &&
      !activeFilters.includes(
        CustomerSummaryFilterEnum.disableAIServiceVersionUserOverride
      )
    ) {
      filteredCustomerSummary = filteredCustomerSummary.filter(
        (customerSummary) =>
          customerSummary[CubeDimensionEnum.modelVersion] ===
          currAIServiceVersionOverride
      );
    }

    // Filter by highest model version bc sometimes we have multiple versions of the same model
    if (
      activeFilters.includes(CustomerSummaryFilterEnum.highestAIServiceVersion)
    ) {
      const timeBefore = performance.now();
      filteredCustomerSummary = filterScansByModelVersion(
        filteredCustomerSummary
      );
      const timeAfter = performance.now();
      if (Developer) {
        console.log(
          `Filtering by highest model version took ${timeAfter - timeBefore} ms`
        );
      }
    }

    if (
      currRanchId &&
      activeFilters.includes(CustomerSummaryFilterEnum.ranch)
    ) {
      filteredCustomerSummary = filteredCustomerSummary.filter(
        (customerSummary) =>
          customerSummary[CubeDimensionEnum.detectionFactRanchId] ===
          currRanchId
      );
    }

    if (
      currBlockId &&
      activeFilters.includes(CustomerSummaryFilterEnum.block)
    ) {
      filteredCustomerSummary = filteredCustomerSummary.filter(
        (customerSummary) =>
          customerSummary[CubeDimensionEnum.detectionFactBlockId] ===
          currBlockId
      );
    }

    if (currScanId && activeFilters.includes(CustomerSummaryFilterEnum.scan)) {
      filteredCustomerSummary = filteredCustomerSummary.filter(
        (customerSummary) =>
          customerSummary[CubeDimensionEnum.detectionFactScanId] === currScanId
      );
    }

    if (activeFilters.includes(CustomerSummaryFilterEnum.showLatestOnly)) {
      const blockIdAndClassNameToLatestSummary = filteredCustomerSummary.reduce(
        (acc, curr) => {
          const key = `${curr[CubeDimensionEnum.detectionFactBlockId]}${
            curr[CubeDimensionEnum.className]
          }`;
          const existing = acc.get(key);
          if (
            !existing ||
            new Date(curr[CubeDimensionEnum.scanDate]) >
              new Date(existing[CubeDimensionEnum.scanDate])
          ) {
            acc.set(key, curr);
          }
          return acc;
        },
        new Map<string, CustomerSummary>()
      );

      filteredCustomerSummary = Array.from(
        blockIdAndClassNameToLatestSummary.values()
      );
    }

    if (
      currFeature &&
      currFeature !== FeatureEnum.none &&
      activeFilters.includes(CustomerSummaryFilterEnum.feature)
    ) {
      // Map the featureFilter to its corresponding alias, or get the same value if no alias exists
      const aliasFilter = mapToAlias(currFeature);

      // Get all associated features from the FeatureAliases if they exist
      const associatedFeatures = FeatureAliases[aliasFilter]
        ? FeatureAliases[aliasFilter]
        : [aliasFilter];

      // Filter the customer summaries based on the alias filter and associated features
      filteredCustomerSummary = filteredCustomerSummary.filter(
        (customerSummary) =>
          associatedFeatures.includes(
            customerSummary[CubeDimensionEnum.className] as FeatureEnum
          )
      );
    }

    return filteredCustomerSummary;
  }, [
    customerSummary.data,
    activeFilters,
    currRanchId,
    currBlockId,
    currScanId,
    currFeature,
    currAIServiceVersionOverride,
    filterByCropTypes,
    blocks,
    cropTypes,
    dateRange,
    Developer,
  ]);
};
